import { Divider, Text } from '@mantine/core';
import * as React from 'react';
import { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { formatToPascalCase } from '../../../../../../react-commons/utils/utilFunctions';
import { SplitPane } from '../../../../../../react-commons/views/split-pane/SplitPane';
import {
    MutationValidationResource,
    SideEffectDeletionValidationResult,
    SideEffectValidationResult,
} from '../../../../resource/resources/validation/mutationValidationResource';
import { betterMathDotRandom } from '../../../util/utilFunctions';
import { MutationValidationResultComponent } from './MutationValidationResultComponent';

type SideEffectResultComponentTreeNode = SerializableRenderTreeNode<any>; // TODO

export const SideEffectResultComponent = ({
    sideEffectResult,
}: {
    sideEffectResult: Record<string, Array<SideEffectValidationResult | SideEffectDeletionValidationResult>> | null;
}) => {
    const [nodes, setNodes] = React.useState<Array<SideEffectResultComponentTreeNode>>([]);
    const [selectedNode, setSelectedNode] = React.useState<{ selectedNode: SideEffectResultComponentTreeNode | null }>({
        selectedNode: null,
    });
    const [selectedValidationResource, setSelectedValidationResource] = React.useState<
        MutationValidationResource | undefined
    >(undefined);

    React.useEffect(() => {
        if (sideEffectResult) {
            const parsedNodes = parseResourceTypes();
            setNodes(parsedNodes);
            if (parsedNodes.length > 0) {
                setSelectedNode({ selectedNode: parsedNodes[0] });
            }
        }
    }, [sideEffectResult]);

    React.useEffect(() => {
        if (selectedNode.selectedNode?.data) {
            setSelectedValidationResource(selectedNode.selectedNode.data.validationResult);
        }
    }, [selectedNode]);

    const parseResourceTypes = () => {
        if (!sideEffectResult) {
            return [];
        }

        return Object.keys(sideEffectResult)
            .filter((domainType) => domainType !== undefined)
            .map((domainType) => {
                const domainTypeChildren = sideEffectResult[domainType].map(
                    (value: SideEffectValidationResult | SideEffectDeletionValidationResult) => {
                        const extract = 'extract' in value ? value.extract : null;
                        const key =
                            extract ? extract.id : `${value.persistableType}_${betterMathDotRandom().toString()}`;
                        const label =
                            extract?.name ??
                            (value.persistableType ? formatToPascalCase(value.persistableType) : undefined) ??
                            extract?.id ??
                            '';
                        return {
                            key: `key_${key}`,
                            label,
                            data: {
                                extract,
                                validationResult: value.validationResult,
                                domainType,
                            },
                            children: [],
                        };
                    }
                );
                const defaultValue = sideEffectResult[domainType][0];
                const defaultExtract = 'extract' in defaultValue ? defaultValue.extract : null;

                return {
                    key: `key_${domainType}`,
                    label: formatToPascalCase(domainType),
                    data:
                        defaultValue ?
                            {
                                extract: defaultExtract,
                                validationResult: defaultValue.validationResult,
                                domainType,
                            }
                        :   undefined,
                    children: domainTypeChildren,
                };
            });
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: 500,
            }}
        >
            <div
                style={{
                    overflow: 'hidden',
                    display: 'inline-block',
                    verticalAlign: 'top',
                    width: '100%',
                    flex: 1,
                }}
            >
                <SplitPane secondaryInitialSize={70}>
                    <div>
                        <Text style={{ paddingLeft: 5 }} c="dimmed">
                            Affected Resource Types
                        </Text>
                        <Divider style={{ marginBottom: 5 }} />
                        <Tree
                            nodes={nodes}
                            selectedNodeKey={selectedNode.selectedNode?.key}
                            onNodeSelected={(node) => {
                                setSelectedNode({ selectedNode: node });
                            }}
                        />
                    </div>

                    <div
                        style={{
                            display: 'inline-block',
                            width: '100%',
                            height: '100%',
                            overflowY: 'auto',
                        }}
                    >
                        <MutationValidationResultComponent
                            resourceValidation={selectedValidationResource}
                            resourceType={selectedNode.selectedNode?.data?.domainType.split(' via')[0] ?? ''}
                        />
                    </div>
                </SplitPane>
            </div>
        </div>
    );
};
